"use client";

import { useRef, useState, useEffect } from "react";
import {
    m,
    useScroll,
    useMotionValueEvent,
    AnimatePresence,
} from "motion/react";

// ─── DATA ─────────────────────────────────────────────────────────────────────

const steps = [
    {
        letter: "D",
        tag: "Etapa 01",
        title: "Descoberta",
        description:
            "Mergulhamos fundo no seu negócio, mercado e concorrência. Mapeamos o perfil do seu cliente ideal, identificamos gaps de posicionamento e definimos metas de crescimento com base em dados reais — não em achismos.",
    },
    {
        letter: "A",
        tag: "Etapa 02",
        title: "Atração",
        description:
            "Criamos o ecossistema de geração de demanda: criativos que param o scroll, campanhas de tráfego pago otimizadas, parcerias em TV e outdoor para alcance massivo, e conteúdo estratégico que posiciona sua marca como referência.",
    },
    {
        letter: "C",
        tag: "Etapa 03",
        title: "Consideração",
        description:
            "Nutrimos os leads com sequências de automação inteligente, páginas de conversão otimizadas e provas sociais estrategicamente posicionadas. Seu prospect chega ao vendedor já convencido e com objeções reduzidas.",
    },
    {
        letter: "A",
        tag: "Etapa 04",
        title: "Ação",
        description:
            "Otimizamos o processo comercial com dashboards em tempo real, scripts de vendas, treinamento de time e integrações com CRM. A conversão deixa de ser obra do acaso e passa a ser previsível e escalável.",
    },
    {
        letter: "A",
        tag: "Etapa 05",
        title: "Apologia",
        description:
            "Transformamos clientes satisfeitos em promotores ativos da sua marca. Programas de indicação, depoimentos estratégicos, UGC e campanhas de retenção que transformam sua base atual no melhor ativo de marketing que existe.",
    },
];

// ─── STEP CARD ─────────────────────────────────────────────────────────────────

function StepCard({
    step,
    index,
    total,
    direction,
}: {
    step: (typeof steps)[0];
    index: number;
    total: number;
    direction: number;
}) {
    return (
        <m.div
            key={index}
            initial={{ opacity: 0, y: direction >= 0 ? 60 : -60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: direction >= 0 ? -60 : 60, scale: 0.95 }}
            transition={{
                duration: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="absolute inset-0 flex items-center justify-center px-4 sm:px-6"
        >
            <div className="w-full max-w-4xl p-6 sm:p-8 md:p-12 rounded-3xl bg-neutral-950/90 backdrop-blur-sm border border-neutral-800 overflow-hidden relative">
                {/* Top gold line */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-midas-yellow-500/60 to-transparent" />
                {/* Corner glow */}
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-midas-yellow-500/[0.04] blur-[80px] rounded-full pointer-events-none" />

                <div className="relative z-10 flex flex-col md:flex-row items-start gap-4 sm:gap-6 md:gap-10">
                    {/* Letter badge */}
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-midas-yellow-400 to-amber-600 flex items-center justify-center font-bold text-2xl sm:text-3xl md:text-4xl text-black flex-shrink-0 shadow-[0_0_30px_rgba(245,197,24,0.25)]">
                        {step.letter}
                    </div>

                    <div className="flex-1 min-w-0">
                        <span className="text-midas-yellow-500 font-mono text-xs tracking-[3px] uppercase">
                            {step.tag}
                        </span>
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mt-2 mb-3 sm:mb-4 uppercase tracking-wide">
                            {step.title}
                        </h3>
                        <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-2xl">
                            {step.description}
                        </p>
                    </div>
                </div>

                {/* Step counter */}
                <div className="absolute bottom-4 right-6 text-neutral-700 font-mono text-xs tracking-wider">
                    {String(index + 1).padStart(2, "0")} /{" "}
                    {String(total).padStart(2, "0")}
                </div>
            </div>
        </m.div>
    );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function MethodologySection() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"],
    });

    // Derive active index from scroll progress
    useMotionValueEvent(scrollYProgress, "change", (latest) => {
        const total = steps.length;
        // Map [0, 1] → [0, total-1]
        // Each step occupies 1/total of the scroll
        const newIndex = Math.min(
            total - 1,
            Math.floor(latest * total)
        );

        setActiveIndex((prev) => {
            if (prev !== newIndex) {
                setDirection(newIndex > prev ? 1 : -1);
            }
            return newIndex;
        });
    });

    const activeStep = steps[activeIndex];

    return (
        <section ref={containerRef} className="h-[500vh] relative z-20 bg-black">
            <div className="sticky top-0 h-screen overflow-hidden will-change-transform">
                {/* Background effects */}
                <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.04] pointer-events-none" />
                <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-midas-yellow-500/[0.03] blur-[150px] rounded-full pointer-events-none" />

                {/* Header — stays fixed at top */}
                <div className="absolute top-0 left-0 right-0 z-20 pt-12 sm:pt-16 pb-6 sm:pb-8 bg-gradient-to-b from-black via-black/90 to-transparent pointer-events-none">
                    <div className="max-w-7xl mx-auto px-6 text-center">
                        <span className="text-midas-yellow-500 font-mono text-xs tracking-[4px] uppercase mb-4 block">
                            Nossa Metodologia
                        </span>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                            O{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-midas-yellow-400 to-amber-500">
                                Método D&apos;Or
                            </span>
                        </h2>
                    </div>
                </div>

                {/* Progress Bar */}
                <m.div
                    style={{ scaleX: scrollYProgress }}
                    className="absolute top-0 left-0 right-0 h-1 bg-midas-yellow-500 origin-left z-30 shadow-[0_0_20px_var(--midas-yellow-500)]"
                />

                {/* Step indicators (dots on the right) */}
                <div className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2.5">
                    {steps.map((_, i) => (
                        <div
                            key={i}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${i === activeIndex
                                ? "bg-midas-yellow-500 scale-150 shadow-[0_0_8px_rgba(245,197,24,0.5)]"
                                : "bg-neutral-600 scale-100"
                                }`}
                        />
                    ))}
                </div>

                {/* Cards — AnimatePresence ensures only ONE card is visible at a time */}
                <div className="relative w-full h-full max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-center z-10">
                    <AnimatePresence mode="wait" initial={false}>
                        <StepCard
                            key={activeIndex}
                            step={activeStep}
                            index={activeIndex}
                            total={steps.length}
                            direction={direction}
                        />
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
